import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Skeleton } from "antd";
import ConsentEventRenderer from "./ConsentEventRenderer";
import { CONSENT_RENDERING_TYPES, HIGHLIGHT_CONSENT_EVENT_STORAGE_KEY } from "../../constant/consent";
import { useTranslation } from "react-i18next";
import { GRAY_200, ERROR_50, PRIMARY_600, BLACK } from "../../constant/colors";
import { fontWeights, sizes } from "../../constant/styles";
import { ReactComponent as HazardTriangleIcon } from "../../assets/svg/hazardTriangle.svg";

const EVENT_DIVIDER = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${GRAY_200};
  flex-shrink: 0;
`;

const SkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
`;

const SkeletonRow = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

const SkeletonContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
`;

const ErrorAlert = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background-color: ${ERROR_50};
  border-radius: 8px;
`;

const ErrorIconWrapper = styled.div`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ErrorMessage = styled.div`
  flex: 1;
  font-size: ${sizes.small};
  font-weight: ${fontWeights.regular};
  line-height: 1.5;
  color: ${BLACK};
`;

const RetryButton = styled.button`
  flex-shrink: 0;
  font-size: ${sizes.small};
  font-weight: ${fontWeights.semibold};
  line-height: 1.5;
  color: ${PRIMARY_600};
  background: none;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  padding: 0;
`;

const SKELETON_ITEMS_COUNT = 4;

const ConsentHistorySkeleton = () => (
  <>
    {Array(SKELETON_ITEMS_COUNT)
      .fill(0)
      .map((_, index) => (
        <React.Fragment key={index}>
          <SkeletonContainer>
            <SkeletonRow>
              <Skeleton.Avatar active size={32} shape="circle" />
              <SkeletonContent>
                <Skeleton.Input active size="small" style={{ width: "100%", height: "24px" }} />
                <Skeleton.Input active size="small" style={{ width: "60%", height: "21px" }} />
              </SkeletonContent>
            </SkeletonRow>
          </SkeletonContainer>
          {index < SKELETON_ITEMS_COUNT - 1 && <EVENT_DIVIDER />}
        </React.Fragment>
      ))}
  </>
);

const ConsentHistoryContent = ({
  historyLoading,
  historyError,
  consentHistory,
  canViewOrDownloadAttachments,
  onRetry
}) => {
  const { t } = useTranslation();
  const [highlightEventId, setHighlightEventId] = useState(null);

  // Read from sessionStorage when component mounts and history is available
  useEffect(() => {
    const storedEventId = sessionStorage.getItem(HIGHLIGHT_CONSENT_EVENT_STORAGE_KEY);
    if (storedEventId && consentHistory.length > 0) {
      setHighlightEventId(storedEventId);
      sessionStorage.removeItem(HIGHLIGHT_CONSENT_EVENT_STORAGE_KEY);
    }
  }, [consentHistory]);

  // Scroll to highlighted event
  useEffect(() => {
    if (highlightEventId) {
      // Delay to ensure drawer animation completes and DOM is fully rendered
      // Drawer has opening animation, and we need the element to be in the DOM before scrolling
      const timer = setTimeout(() => {
        const element = document.getElementById(`consent-event-${highlightEventId}`);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [highlightEventId]);

  if (historyLoading) {
    return <ConsentHistorySkeleton />;
  }

  if (historyError) {
    return (
      <ErrorAlert>
        <ErrorIconWrapper>
          <HazardTriangleIcon />
        </ErrorIconWrapper>
        <ErrorMessage>{t("consent.error.loading_history")}</ErrorMessage>
        <RetryButton onClick={onRetry}>{t("consent.error.retry")}</RetryButton>
      </ErrorAlert>
    );
  }

  if (consentHistory.length === 0) {
    return <ConsentEventRenderer event={{ type: CONSENT_RENDERING_TYPES.NO_RECORD }} />;
  }

  return (
    <>
      {consentHistory.map((event, index) => {
        const isHighlighted = Boolean(highlightEventId && event.id === parseInt(highlightEventId));
        return (
          <React.Fragment key={event.id}>
            <ConsentEventRenderer
              event={event}
              isHighlighted={isHighlighted}
              canViewOrDownloadAttachments={canViewOrDownloadAttachments}
            />
            {index < consentHistory.length - 1 && <EVENT_DIVIDER />}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default ConsentHistoryContent;
